import { useState, useRef, useCallback, useEffect } from 'react'
import { Stage, Layer, Image as KonvaImage, Rect, Transformer } from 'react-konva'
import useImage from 'use-image'
import { Upload, ZoomIn, ZoomOut, RotateCw, Check, ShoppingCart, Save, AlertTriangle, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { MAT_SIZES, type MatSize, type MatThickness, type MatMaterial, type StitchingColor } from '../types'
import { useCartStore, useAuthStore } from '../context/store'
import { useLocation } from 'react-router-dom'
import { customerApi, makePreview, ApiError, type SavedDesignRecord } from '../utils/customer'
import SceneMockup, { type SceneCorners } from '../components/SceneMockup'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { useSeo } from '../hooks/useSeo'

const SIZES: MatSize[] = ['Small', 'Medium', 'Large', 'XL', 'XXL', '3XL']
const THICKNESSES: MatThickness[] = ['3mm', '4mm', '6mm']
const MATERIALS: MatMaterial[] = ['Cloth Standard', 'Cloth Premium', 'Hard Surface', 'RGB Edge']
const STITCHING_COLORS: StitchingColor[] = ['Black', 'White', 'Purple', 'Red', 'Blue', 'Gold']
const STITCHING_COLOR_MAP: Record<StitchingColor, string> = {
  Black: '#111111', White: '#FFFFFF', Purple: '#7C3AED', Red: '#EF4444', Blue: '#3B82F6', Gold: '#F59E0B',
}

const PRICE_BASE = 29.99
const SIZE_PRICE: Record<MatSize, number> = { Small: 0, Medium: 5, Large: 10, XL: 18, XXL: 24, '3XL': 35 }
const THICKNESS_PRICE: Record<MatThickness, number> = { '3mm': 0, '4mm': 3, '6mm': 6 }
const MATERIAL_PRICE: Record<MatMaterial, number> = {
  'Cloth Standard': 0, 'Cloth Premium': 8, 'Hard Surface': 12, 'RGB Edge': 20,
}

/**
 * Scene base definitions. The actual image path and corners are computed
 * per-size in getScenesForSize() below.
 */
const SCENE_BASES = [
  { id: 'gaming',  label: 'Gaming Desk',  file: 'scene1-gaming-34angle',  aspectRatio: 16 / 9 },
  { id: 'minimal', label: 'Minimal Desk', file: 'scene2-minimal-desk',    aspectRatio: 16 / 9 },
  { id: 'topdown', label: 'Top-Down',     file: 'scene3-topdown',          aspectRatio: 1      },
  { id: 'closeup', label: 'Close-Up',     file: 'scene4-closeup-side',     aspectRatio: 16 / 9 },
  { id: 'dual',    label: 'Dual Monitor', file: 'scene5-dual-monitor',     aspectRatio: 16 / 9 },
]

/** User-picked corners for every size × scene combination */
const ALL_CORNERS: Record<string, Record<string, SceneCorners>> = {
  small: {
    gaming:  { tl: [0.3488, 0.4897], tr: [0.507,  0.4091], br: [0.6488, 0.5888], bl: [0.4756, 0.7087] },
    minimal: { tl: [0.5419, 0.4649], tr: [0.6907, 0.562],  br: [0.5872, 0.7851], bl: [0.4291, 0.6591] },
    topdown: { tl: [0.3698, 0.3628], tr: [0.686,  0.3651], br: [0.6884, 0.7256], bl: [0.3733, 0.7279] },
    closeup: { tl: [0.386,  0.4566], tr: [0.5953, 0.5826], br: [0.4116, 0.843],  bl: [0.1977, 0.6302] },
    dual:    { tl: [0.4012, 0.5331], tr: [0.607,  0.5289], br: [0.6314, 0.7789], bl: [0.3872, 0.7913] },
  },
  medium: {
    gaming:  { tl: [0.3186, 0.5041], tr: [0.5384, 0.3864], br: [0.6849, 0.5682], bl: [0.4442, 0.7376] },
    minimal: { tl: [0.514,  0.4442], tr: [0.7244, 0.5785], br: [0.6221, 0.8099], bl: [0.4,    0.6302] },
    topdown: { tl: [0.3174, 0.3628], tr: [0.6767, 0.3663], br: [0.6802, 0.7291], bl: [0.3256, 0.7291] },
    closeup: { tl: [0.4814, 0.2335], tr: [0.7547, 0.4669], br: [0.4605, 0.6653], bl: [0.2116, 0.3595] },
    dual:    { tl: [0.3581, 0.531],  tr: [0.6512, 0.5331], br: [0.6791, 0.7831], bl: [0.3384, 0.7872] },
  },
  large: {
    gaming:  { tl: [0.264,  0.4835], tr: [0.5442, 0.3409], br: [0.7395, 0.5661], bl: [0.4267, 0.7955] },
    minimal: { tl: [0.5,    0.3864], tr: [0.7721, 0.562],  br: [0.6372, 0.8781], bl: [0.3453, 0.6426] },
    topdown: { tl: [0.2628, 0.3012], tr: [0.7233, 0.3047], br: [0.7291, 0.7919], bl: [0.2686, 0.7884] },
    closeup: { tl: [0.4849, 0.1736], tr: [0.843,  0.4649], br: [0.4442, 0.7397], bl: [0.1326, 0.3326] },
    dual:    { tl: [0.3221, 0.4917], tr: [0.686,  0.4938], br: [0.7326, 0.8264], bl: [0.2837, 0.8244] },
  },
  xl: {
    gaming:  { tl: [0.1578, 0.5385], tr: [0.6511, 0.2875], br: [0.8611, 0.4773], bl: [0.3078, 0.8903] },
    minimal: { tl: [0.3967, 0.3231], tr: [0.8767, 0.6314], br: [0.7478, 0.9674], bl: [0.2278, 0.5484] },
    topdown: { tl: [0.0933, 0.3028], tr: [0.9044, 0.3039], br: [0.9044, 0.7883], bl: [0.0922, 0.7872] },
    closeup: { tl: [0.3456, 0.0682], tr: [0.9844, 0.5781], br: [0.5611, 0.8982], bl: [0.0122, 0.1729] },
    dual:    { tl: [0.1822, 0.4891], tr: [0.8289, 0.4891], br: [0.9067, 0.8271], bl: [0.1078, 0.8251] },
  },
  xxl: {
    gaming:  { tl: [0.1256, 0.5537], tr: [0.6826, 0.2748], br: [0.8919, 0.4483], bl: [0.2721, 0.9112] },
    minimal: { tl: [0.3663, 0.3058], tr: [0.907,  0.6529], br: [0.7837, 0.9897], bl: [0.1953, 0.5186] },
    topdown: { tl: [0.0407, 0.3023], tr: [0.9558, 0.3035], br: [0.9593, 0.7872], bl: [0.0442, 0.7872] },
    closeup: { tl: [0.3047, 0.0351], tr: [0.993,  0.5868], br: [0.5779, 0.9236], bl: [0.0023, 0.124]  },
    dual:    { tl: [0.1395, 0.4917], tr: [0.8698, 0.4897], br: [0.9593, 0.8306], bl: [0.0593, 0.8285] },
  },
  '3xl': {
    gaming:  { tl: [0.0012, 0.5021], tr: [0.7174, 0.188],  br: [0.9977, 0.376],  bl: [0.1651, 0.9752] },
    minimal: { tl: [0.3174, 0.1983], tr: [0.9942, 0.6012], br: [0.9023, 0.9959], bl: [0.0791, 0.4773] },
    topdown: { tl: [0.0105, 0.186],  tr: [0.9895, 0.1814], br: [0.9895, 0.914],  bl: [0.0058, 0.9151] },
    closeup: { tl: [0.036,  0.0702], tr: [0.9802, 0.4773], br: [0.8116, 0.9897], bl: [0.0174, 0.4917] },
    dual:    { tl: [0.0465, 0.3926], tr: [0.957,  0.3967], br: [0.9919, 0.9318], bl: [0.014,  0.9277] },
  },
}

const SIZE_ID_MAP: Record<MatSize, string> = {
  Small: 'small', Medium: 'medium', Large: 'large', XL: 'xl', XXL: 'xxl', '3XL': '3xl',
}

function getScenesForSize(matSize: MatSize) {
  const sizeId = SIZE_ID_MAP[matSize]
  return SCENE_BASES.map((base) => ({
    ...base,
    image: `/scenes/${base.file}-${sizeId}.jpg`,
    corners: ALL_CORNERS[sizeId][base.id],
  }))
}

const CANVAS_W = 480
const CANVAS_H = 320

/** Width of a container element, kept in sync with a ResizeObserver. */
function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

// Inner component for Konva image (needs hook)
function DesignImage({
  src, x, y, scaleX, scaleY, rotation, isSelected, onSelect, onChange,
}: {
  src: string; x: number; y: number; scaleX: number; scaleY: number; rotation: number;
  isSelected: boolean; onSelect: () => void; onChange: (attrs: Record<string, number>) => void;
}) {
  const [image] = useImage(src, 'anonymous')
  const imgRef = useRef<any>(null)
  const trRef = useRef<any>(null)

  useEffect(() => {
    if (isSelected && trRef.current && imgRef.current) {
      trRef.current.nodes([imgRef.current])
      trRef.current.getLayer().batchDraw()
    }
  }, [isSelected])

  if (!image) return null

  return (
    <>
      <KonvaImage
        ref={imgRef}
        image={image}
        x={x}
        y={y}
        scaleX={scaleX}
        scaleY={scaleY}
        rotation={rotation}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => onChange({ x: e.target.x(), y: e.target.y() })}
        onTransformEnd={(e) => {
          const node = imgRef.current
          onChange({
            x: node.x(), y: node.y(),
            scaleX: node.scaleX(), scaleY: node.scaleY(),
            rotation: node.rotation(),
          })
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 20 || newBox.height < 20) return oldBox
            return newBox
          }}
        />
      )}
    </>
  )
}

export default function CustomizerPage() {
  useSeo({ title: 'Design Your Custom Mouse Mat', description: 'Upload your artwork, pick a size, thickness, material and stitching, and preview it on real desk scenes before you order.', path: '/customize' })
  const [step, setStep] = useState<'upload' | 'edit' | 'preview' | 'confirm'>('upload')
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [imageAttrs, setImageAttrs] = useState({ x: 40, y: 40, scaleX: 1, scaleY: 1, rotation: 0 })
  const [isSelected, setIsSelected] = useState(true)
  const [size, setSize] = useState<MatSize>('XL')
  const [thickness, setThickness] = useState<MatThickness>('4mm')
  const [material, setMaterial] = useState<MatMaterial>('Cloth Standard')
  const [stitching, setStitching] = useState<StitchingColor>('Black')
  const [activeScene, setActiveScene] = useState(0)
  const [confirmed, setConfirmed] = useState(false)
  const [confirmedResponsibility, setConfirmedResponsibility] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [designName, setDesignName] = useState('')
  const [savingDesign, setSavingDesign] = useState(false)
  const { isAuthenticated, login } = useAuthStore()
  const location = useLocation()
  const stageRef = useRef<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // The Konva canvas keeps a fixed 480x320 logical coordinate space and is
  // scaled down to fit narrow screens, so saved positions are device-independent.
  const [canvasWrapRef, canvasWrapWidth] = useContainerWidth<HTMLDivElement>()
  const canvasScale = canvasWrapWidth > 0 ? Math.min(1, canvasWrapWidth / CANVAS_W) : 1
  const { openCart } = useCartStore()

  const price = PRICE_BASE + SIZE_PRICE[size] + THICKNESS_PRICE[thickness] + MATERIAL_PRICE[material]

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file.')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image must be under 20MB.')
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => {
      setUploadedImage(ev.target?.result as string)
      setImageAttrs({ x: 20, y: 20, scaleX: 0.8, scaleY: 0.8, rotation: 0 })
      setStep('edit')
    }
    reader.readAsDataURL(file)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      const fakeEvent = { target: { files: [file] } } as any
      handleFileUpload(fakeEvent)
    }
  }, [handleFileUpload])

  const adjustScale = (delta: number) => {
    setImageAttrs((prev) => ({
      ...prev,
      scaleX: Math.max(0.1, Math.min(5, prev.scaleX + delta)),
      scaleY: Math.max(0.1, Math.min(5, prev.scaleY + delta)),
    }))
  }

  const adjustRotation = (delta: number) => {
    setImageAttrs((prev) => ({ ...prev, rotation: (prev.rotation + delta) % 360 }))
  }

  const handleAddToCart = () => {
    if (!confirmed || !confirmedResponsibility) {
      toast.error('Please confirm both checkboxes before adding to cart.')
      return
    }
    setShowConfirmModal(false)
    toast.success('Custom mat request received! We will contact you to confirm your order.')
    openCart()
  }

  const handleSaveDesign = async () => {
    if (!uploadedImage) return
    if (!isAuthenticated) {
      toast('Sign in to save your design', { icon: '🔒' })
      login('/customize')
      return
    }
    setSavingDesign(true)
    try {
      const preview = await makePreview(uploadedImage)
      await customerApi.saveDesign({
        name: designName.trim() || `Custom mat — ${new Date().toLocaleDateString()}`,
        preview,
        variant: { size, thickness, material, stitching },
        imageAttrs,
      })
      toast.success('Design saved to your account')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save design')
    } finally {
      setSavingDesign(false)
    }
  }

  // Reopen a saved design passed from the Saved Designs page
  useEffect(() => {
    const saved = (location.state as { savedDesign?: SavedDesignRecord } | null)?.savedDesign
    if (!saved) return
    setUploadedImage(saved.preview)
    if (saved.imageAttrs) setImageAttrs(saved.imageAttrs)
    if (saved.variant.size) setSize(saved.variant.size)
    if (saved.variant.thickness) setThickness(saved.variant.thickness)
    if (saved.variant.material) setMaterial(saved.variant.material)
    if (saved.variant.stitching) setStitching(saved.variant.stitching)
    setDesignName(saved.name)
    setStep('edit')
    toast('Saved design loaded. Re-upload your original file for full print quality.', { icon: '📂', duration: 5000 })
    // Clear the navigation state so a refresh doesn't reload it
    window.history.replaceState({}, '')
  }, [location.state])

  const SCENES = getScenesForSize(size)
  const scene = SCENES[activeScene]

  return (
    <div className="min-h-screen py-6 sm:py-8 pb-28 lg:pb-8">
      <div className="container-main">
        {/* Header */}
        <div className="mb-8">
          <div className="badge-purple inline-flex items-center gap-1.5 px-3 py-1 mb-3 text-xs">
            <Sparkles size={12} /> Custom Mat Builder
          </div>
          <h1 className="section-title">Design Your Mat</h1>
          <p className="section-subtitle">Upload your image, configure your mat, and preview it in real desk scenes.</p>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto no-scrollbar">
          {[
            { id: 'upload', label: '1. Upload Image' },
            { id: 'edit', label: '2. Position & Edit' },
            { id: 'preview', label: '3. Preview Scenes' },
            { id: 'confirm', label: '4. Confirm & Order' },
          ].map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => uploadedImage && setStep(s.id as any)}
                disabled={!uploadedImage && s.id !== 'upload'}
                className={clsx(
                  'px-3 sm:px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                  step === s.id
                    ? 'bg-brand-purple text-white'
                    : uploadedImage
                    ? 'text-brand-gray hover:text-white'
                    : 'text-brand-gray/40 cursor-not-allowed'
                )}
              >
                {s.label}
              </button>
              {i < 3 && <ChevronRight size={14} className="text-brand-border flex-shrink-0" />}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ─── Main Canvas Area ──────────────────────────────────────── */}
          <div className="lg:col-span-2">

            {/* Step 1: Upload */}
            {step === 'upload' && (
              <div
                className="border-2 border-dashed border-brand-border rounded-2xl p-6 sm:p-12 text-center hover:border-brand-purple transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="w-16 h-16 bg-brand-purple/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Upload size={28} className="text-brand-purple" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">Upload Your Image</h3>
                <p className="text-brand-gray text-sm mb-6">
                  Drag and drop or click to browse. PNG, JPG, WEBP — max 20MB.
                </p>
                <button className="btn-primary">
                  <Upload size={16} /> Choose Image
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <p className="text-brand-gray text-xs mt-6">
                  For best results, use a high-resolution image (at least 2000×2000px).
                </p>
              </div>
            )}

            {/* Step 2: Edit Canvas */}
            {step === 'edit' && uploadedImage && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h3 className="font-semibold text-white">Position Your Image</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => adjustScale(-0.1)} className="btn-ghost p-2.5 rounded-lg" title="Zoom Out">
                      <ZoomOut size={16} />
                    </button>
                    <button onClick={() => adjustScale(0.1)} className="btn-ghost p-2.5 rounded-lg" title="Zoom In">
                      <ZoomIn size={16} />
                    </button>
                    <button onClick={() => adjustRotation(15)} className="btn-ghost p-2.5 rounded-lg" title="Rotate">
                      <RotateCw size={16} />
                    </button>
                    <button onClick={() => adjustRotation(-15)} className="btn-ghost p-2.5 rounded-lg" title="Rotate CCW">
                      <RotateCw size={16} className="scale-x-[-1]" />
                    </button>
                  </div>
                </div>

                {/* Konva Canvas */}
                <div ref={canvasWrapRef} className="rounded-xl overflow-hidden border border-brand-border bg-zinc-900 touch-none">
                  <Stage
                    ref={stageRef}
                    width={Math.round(CANVAS_W * canvasScale)}
                    height={Math.round(CANVAS_H * canvasScale)}
                    scaleX={canvasScale}
                    scaleY={canvasScale}
                    onMouseDown={(e) => {
                      if (e.target === e.target.getStage()) setIsSelected(false)
                    }}
                    onTouchStart={(e) => {
                      if (e.target === e.target.getStage()) setIsSelected(false)
                    }}
                  >
                    <Layer>
                      {/* Mat background */}
                      <Rect
                        x={0} y={0}
                        width={CANVAS_W} height={CANVAS_H}
                        fill="#1a1a1a"
                        cornerRadius={8}
                      />
                      {/* Mat border (stitching preview) */}
                      <Rect
                        x={6} y={6}
                        width={CANVAS_W - 12} height={CANVAS_H - 12}
                        stroke={STITCHING_COLOR_MAP[stitching]}
                        strokeWidth={3}
                        fill="transparent"
                        cornerRadius={6}
                        dash={[8, 4]}
                      />
                      {/* User image */}
                      <DesignImage
                        src={uploadedImage}
                        x={imageAttrs.x}
                        y={imageAttrs.y}
                        scaleX={imageAttrs.scaleX}
                        scaleY={imageAttrs.scaleY}
                        rotation={imageAttrs.rotation}
                        isSelected={isSelected}
                        onSelect={() => setIsSelected(true)}
                        onChange={(attrs) => setImageAttrs((prev) => ({ ...prev, ...attrs }))}
                      />
                    </Layer>
                  </Stage>
                </div>

                <p className="text-brand-gray text-xs mt-3 text-center">
                  <span className="hidden sm:inline">Drag to reposition · Use handles to resize · Toolbar to zoom/rotate</span>
                  <span className="sm:hidden">Drag to move · Pinch handles to resize · Buttons to zoom/rotate</span>
                </p>

                <div className="flex gap-3 mt-4">
                  <button onClick={() => setStep('preview')} className="btn-primary flex-1">
                    Continue to Preview
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Preview Scenes — Real desk scene images with perspective warp */}
            {step === 'preview' && uploadedImage && (
              <div>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="font-semibold text-white">Preview in Scene</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActiveScene((p) => (p - 1 + SCENES.length) % SCENES.length)}
                      className="btn-ghost p-2 rounded-lg"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-brand-gray text-sm">{activeScene + 1} / {SCENES.length}</span>
                    <button
                      onClick={() => setActiveScene((p) => (p + 1) % SCENES.length)}
                      className="btn-ghost p-2 rounded-lg"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                {/* Real Scene Preview with Perspective Warp */}
                <SceneMockup
                  key={scene.id}
                  sceneImage={scene.image}
                  designImage={uploadedImage}
                  corners={scene.corners}
                  label={scene.label}
                  stitchingColor={STITCHING_COLOR_MAP[stitching]}
                  aspectRatio={scene.aspectRatio}
                />

                {/* Scene Dots */}
                <div className="flex justify-center gap-2 mt-4">
                  {SCENES.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => setActiveScene(i)}
                      className={clsx(
                        'w-2 h-2 rounded-full transition-all',
                        activeScene === i ? 'bg-brand-purple w-6' : 'bg-brand-border hover:bg-brand-gray'
                      )}
                    />
                  ))}
                </div>

                {/* Scene Tabs */}
                <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar">
                  {SCENES.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => setActiveScene(i)}
                      className={clsx(
                        'flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium border transition-all',
                        activeScene === i
                          ? 'bg-brand-purple border-brand-purple text-white'
                          : 'border-brand-border text-brand-gray hover:text-white'
                      )}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                {/* All Scenes Thumbnail Strip */}
                <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar snap-x sm:grid sm:grid-cols-5 sm:overflow-visible">
                  {SCENES.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => setActiveScene(i)}
                      className={clsx(
                        'w-32 flex-shrink-0 snap-start sm:w-auto rounded-lg overflow-hidden border-2 transition-all',
                        activeScene === i ? 'border-brand-purple' : 'border-brand-border hover:border-brand-purple/50'
                      )}
                    >
                      <SceneMockup
                        key={`thumb-${s.id}-${uploadedImage.slice(0, 20)}`}
                        sceneImage={s.image}
                        designImage={uploadedImage}
                        corners={s.corners}
                        label=""
                        stitchingColor={STITCHING_COLOR_MAP[stitching]}
                        aspectRatio={s.aspectRatio}
                      />
                    </button>
                  ))}
                </div>

                <div className="flex gap-3 mt-6">
                  <button onClick={() => setStep('edit')} className="btn-secondary flex-1">
                    Back to Edit
                  </button>
                  <button onClick={() => setStep('confirm')} className="btn-primary flex-1">
                    Continue to Order
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {step === 'confirm' && uploadedImage && (
              <div>
                <h3 className="font-semibold text-white text-lg mb-6">Review & Confirm Your Order</h3>

                {/* Design Summary with scene preview */}
                <div className="card p-5 mb-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-full sm:w-32 rounded-lg overflow-hidden border border-brand-border flex-shrink-0">
                      <SceneMockup
                        sceneImage={SCENES[0].image}
                        designImage={uploadedImage}
                        corners={SCENES[0].corners}
                        label=""
                        stitchingColor={STITCHING_COLOR_MAP[stitching]}
                        aspectRatio={SCENES[0].aspectRatio}
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Custom Mouse Mat</h4>
                      <div className="text-brand-gray text-sm space-y-0.5">
                        <p>Size: <span className="text-white">{size} ({MAT_SIZES[size].width}×{MAT_SIZES[size].height}mm)</span></p>
                        <p>Thickness: <span className="text-white">{thickness}</span></p>
                        <p>Material: <span className="text-white">{material}</span></p>
                        <p>Stitching: <span className="text-white">{stitching}</span></p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-display text-xl font-bold text-white">${price.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Save Design */}
                <div className="card p-5 mb-6">
                  <h4 className="font-medium text-white mb-3">Save This Design</h4>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={designName}
                      onChange={(e) => setDesignName(e.target.value)}
                      placeholder="Design name (optional)"
                      className="input flex-1"
                    />
                    <button onClick={handleSaveDesign} disabled={savingDesign} className="btn-secondary flex items-center justify-center gap-2 min-h-[44px]">
                      <Save size={16} /> {savingDesign ? 'Saving…' : isAuthenticated ? 'Save' : 'Sign in to save'}
                    </button>
                  </div>
                </div>

                {/* Confirmation Checkboxes */}
                <div className="card p-5 mb-6 border-amber-500/30">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertTriangle size={16} className="text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Before You Order</h4>
                      <p className="text-brand-gray text-sm">Please review and confirm the following before adding to cart.</p>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer group mb-4">
                    <div
                      onClick={() => setConfirmed(!confirmed)}
                      className={clsx(
                        'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all cursor-pointer',
                        confirmed ? 'bg-brand-purple border-brand-purple' : 'border-brand-border group-hover:border-brand-purple'
                      )}
                    >
                      {confirmed && <Check size={12} className="text-white" />}
                    </div>
                    <span className="text-brand-gray text-sm leading-relaxed">
                      I confirm that this is my <strong className="text-white">final design</strong>. I have reviewed the image placement, size, and all options. I understand that custom orders cannot be changed after submission.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div
                      onClick={() => setConfirmedResponsibility(!confirmedResponsibility)}
                      className={clsx(
                        'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all cursor-pointer',
                        confirmedResponsibility ? 'bg-brand-purple border-brand-purple' : 'border-brand-border group-hover:border-brand-purple'
                      )}
                    >
                      {confirmedResponsibility && <Check size={12} className="text-white" />}
                    </div>
                    <span className="text-brand-gray text-sm leading-relaxed">
                      I confirm that I have the <strong className="text-white">right to use this image</strong> and take full responsibility for its content. MatryxLab is not liable for any copyright or intellectual property issues related to my uploaded image.
                    </span>
                  </label>
                </div>

                {/* Add to Cart */}
                <button
                  onClick={handleAddToCart}
                  disabled={!confirmed || !confirmedResponsibility}
                  className={clsx(
                    'btn-primary w-full text-base py-4',
                    confirmed && confirmedResponsibility ? 'glow-purple' : 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <ShoppingCart size={18} />
                  Add to Cart — ${price.toFixed(2)}
                </button>

                {(!confirmed || !confirmedResponsibility) && (
                  <p className="text-brand-gray text-xs text-center mt-3">
                    Please confirm both checkboxes above to proceed.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ─── Right Panel: Variant Configurator ────────────────────── */}
          <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="card p-5">
              <h3 className="font-semibold text-white mb-5">Configure Your Mat</h3>

              {/* Size */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Size</label>
                  <span className="text-brand-gray text-xs">{MAT_SIZES[size].width}×{MAT_SIZES[size].height}mm</span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={clsx(
                        'py-2 px-2 rounded-lg text-xs font-medium border transition-all text-center',
                        size === s
                          ? 'bg-brand-purple border-brand-purple text-white'
                          : 'border-brand-border text-brand-gray hover:border-brand-purple hover:text-white'
                      )}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Thickness */}
              <div className="mb-5">
                <label className="label">Thickness</label>
                <div className="flex gap-1.5">
                  {THICKNESSES.map((t) => (
                    <button
                      key={t}
                      onClick={() => setThickness(t)}
                      className={clsx(
                        'flex-1 py-2 rounded-lg text-xs font-medium border transition-all',
                        thickness === t
                          ? 'bg-brand-purple border-brand-purple text-white'
                          : 'border-brand-border text-brand-gray hover:border-brand-purple hover:text-white'
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Material */}
              <div className="mb-5">
                <label className="label">Material</label>
                <div className="space-y-1.5">
                  {MATERIALS.map((m) => (
                    <button
                      key={m}
                      onClick={() => setMaterial(m)}
                      className={clsx(
                        'w-full py-2 px-3 rounded-lg text-xs font-medium border transition-all text-left flex justify-between items-center',
                        material === m
                          ? 'bg-brand-purple border-brand-purple text-white'
                          : 'border-brand-border text-brand-gray hover:border-brand-purple hover:text-white'
                      )}
                    >
                      <span>{m}</span>
                      {MATERIAL_PRICE[m] > 0 && <span className="opacity-70">+${MATERIAL_PRICE[m]}</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stitching */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Edge Stitching</label>
                  <span className="text-brand-gray text-xs">{stitching}</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {STITCHING_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setStitching(color)}
                      title={color}
                      className={clsx(
                        'w-7 h-7 rounded-full border-2 transition-all relative',
                        stitching === color ? 'border-white scale-110' : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: STITCHING_COLOR_MAP[color] }}
                    >
                      {stitching === color && (
                        <Check
                          size={12}
                          className="absolute inset-0 m-auto"
                          style={{ color: color === 'White' || color === 'Gold' ? '#000' : '#fff' }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              <div className="pt-4 border-t border-brand-border">
                <div className="flex items-center justify-between">
                  <span className="text-brand-gray text-sm">Total</span>
                  <span className="font-display text-2xl font-bold text-white">${price.toFixed(2)}</span>
                </div>
                <p className="text-brand-gray text-xs mt-1">CAD · Free shipping over $75</p>
              </div>
            </div>

            {/* Quick Actions */}
            {uploadedImage && (
              <div className="card p-5 space-y-3">
                <button
                  onClick={() => setStep('edit')}
                  className="btn-ghost w-full justify-center text-sm"
                >
                  Edit Image Position
                </button>
                <button
                  onClick={() => setStep('preview')}
                  className="btn-ghost w-full justify-center text-sm"
                >
                  Preview in Scenes
                </button>
                <button
                  onClick={() => {
                    setUploadedImage(null)
                    setStep('upload')
                  }}
                  className="btn-ghost w-full justify-center text-sm text-red-400 hover:text-red-300"
                >
                  Remove Image
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Mobile sticky price / action bar ──────────────────────────── */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-brand-dark/95 backdrop-blur border-t border-brand-border px-4 py-3 safe-bottom">
        <div className="container-main flex items-center justify-between gap-4">
          <div>
            <p className="text-brand-gray text-xs leading-none mb-1">{size} · {thickness} · {material}</p>
            <p className="font-display text-xl font-bold text-white leading-none">${price.toFixed(2)} <span className="text-brand-gray text-xs font-normal">CAD</span></p>
          </div>
          {step === 'upload' && (
            <button onClick={() => fileInputRef.current?.click()} className="btn-primary min-h-[44px] px-5">Upload Image</button>
          )}
          {step === 'edit' && (
            <button onClick={() => setStep('preview')} className="btn-primary min-h-[44px] px-5">Preview</button>
          )}
          {step === 'preview' && (
            <button onClick={() => setStep('confirm')} className="btn-primary min-h-[44px] px-5">Continue</button>
          )}
          {step === 'confirm' && (
            <button
              onClick={handleAddToCart}
              className="btn-primary min-h-[44px] px-5"
            >
              Order
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
